import { EmergencyNotificationConfiguration } from '@dreryk/egabinet-emergency-notification-frontend-api';
import { PractitionerView } from '@dreryk/egabinet-personnel-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormRouteLeavingPrompt, Separator, StyledFlex } from '@dreryk/react-components';

import { EmergencyNotificationHistoryTable } from '../history/EmergencyNotificationHistoryTable';

import {
   EmergencyNotificationConfigurationFormBean,
   EmergencyNotificationConfigurationFormController,
} from './EmergencyNotificationConfigurationForm.types';
import { StyledForm, StyledIndentedFieldGroup } from './EmergencyNotificationConfigurationForm.styled';
import { EmergencyNotificationSmsConfigurationEnabledField } from './fields/EmergencyNotificationSmsConfigurationEnabledField';
import { EmergencyNotificationConfigurationEnabledField } from './fields/EmergencyNotificationConfigurationEnabledField';
import {
   formInitializer,
   withConfigurationEnabled,
   withInAppConfigurationEnabled,
   withSmsConfigurationEnabled,
} from './EmergencyNotificationConfigurationForm.utils';
import { EmergencyNotificationSmsConfigurationContactPersonsField } from './fields/EmergencyNotificationSmsConfigurationContactPersonsField';
import { EmergencyNotificationSmsConfigurationMessageCustomizablePartsField } from './fields/EmergencyNotificationSmsConfigurationMessageCustomizablePartsField';
import { EmergencyNotificationInAppConfigurationEnabledField } from './fields/EmergencyNotificationInAppConfigurationEnabledField';
import { EmergencyNotificationInAppConfigurationResponseTeamPractitionersField } from './fields/EmergencyNotificationInAppConfigurationResponseTeamPractitionersField';
import { TestEmergencyNotificationButton } from './components/TestEmergencyNotificationButton';

type EmergencyNotificationConfigurationFormRenderProps = Pick<
   EmergencyNotificationConfigurationFormController,
   'handleSubmit'
>;

type EmergencyNotificationConfigurationFormProps = {
   readonly emergencyNotificationConfiguration: EmergencyNotificationConfiguration;
   readonly practitioners: PractitionerView[];
   readonly children: (renderProps: EmergencyNotificationConfigurationFormRenderProps) => JSX.Element;
};

// eslint-disable-next-line max-lines-per-function
export const EmergencyNotificationConfigurationForm = (
   props: EmergencyNotificationConfigurationFormProps
): JSX.Element => {
   const controller =
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useFormController<EmergencyNotificationConfigurationFormBean>(
         formInitializer(props.emergencyNotificationConfiguration, props.practitioners),
         { resetAfterSubmit: true }
      );

   const isConfigurationEnabled = withConfigurationEnabled(controller);

   return (
      <StyledForm noValidate autoComplete={'off'}>
         <EmergencyNotificationConfigurationEnabledField
            register={controller.register}
            watch={controller.watch}
            emergencyNotificationConfiguration={props.emergencyNotificationConfiguration}
         />

         {isConfigurationEnabled ? (
            <StyledFlex direction={'column'} gap={'8px'}>
               <EmergencyNotificationInAppConfigurationEnabledField
                  register={controller.register}
                  emergencyNotificationConfiguration={props.emergencyNotificationConfiguration}
               />
               {withInAppConfigurationEnabled(controller) && (
                  <StyledIndentedFieldGroup>
                     <EmergencyNotificationInAppConfigurationResponseTeamPractitionersField
                        register={controller.register}
                        practitioners={props.practitioners}
                        emergencyNotificationConfiguration={props.emergencyNotificationConfiguration}
                     />
                  </StyledIndentedFieldGroup>
               )}
            </StyledFlex>
         ) : null}

         {isConfigurationEnabled ? (
            <StyledFlex direction={'column'} gap={'8px'}>
               <EmergencyNotificationSmsConfigurationEnabledField
                  register={controller.register}
                  emergencyNotificationConfiguration={props.emergencyNotificationConfiguration}
               />
               {withSmsConfigurationEnabled(controller) && (
                  <StyledIndentedFieldGroup direction={'column'} gap={'16px'}>
                     <EmergencyNotificationSmsConfigurationContactPersonsField
                        register={controller.register}
                        emergencyNotificationConfiguration={props.emergencyNotificationConfiguration}
                     />
                     <EmergencyNotificationSmsConfigurationMessageCustomizablePartsField
                        register={controller.register}
                        watch={controller.watch}
                        setValue={controller.setValue}
                        emergencyNotificationSmsConfiguration={
                           props.emergencyNotificationConfiguration.smsConfiguration
                        }
                     />
                  </StyledIndentedFieldGroup>
               )}
            </StyledFlex>
         ) : null}

         {props.children({
            handleSubmit: controller.handleSubmit,
         })}

         {isConfigurationEnabled ? (
            <>
               <Separator />
               <TestEmergencyNotificationButton />
            </>
         ) : null}

         <Separator />
         <EmergencyNotificationHistoryTable isConfigurationEnabled={isConfigurationEnabled} />

         <FormRouteLeavingPrompt formState={controller.formState} />
      </StyledForm>
   );
};
